import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { TAB_NAMES } from "src/apps/common/menu-navigation/menuNavigation";
import TabHeader from "src/apps/common/tab-header/TabHeader";
import ConfirmationModal from "src/components/modals/ConfirmationModal";
import ReactTable from "src/components/tables/react-table/ReactTable";
import {
  getCityLabel,
  getCountryLabel,
  getFormattedDate,
  getStateLabel,
  handleCsvExport,
} from "src/helpers/helpers";
import useDebounce from "src/hooks/useDebounce";
import useNotification from "src/hooks/useNotification";
import useSetActiveTab from "src/hooks/useSetActiveTab";
import {
  useDeleteStore,
  useGetStores,
  useUpdateStoreStatus,
} from "../api-clients";
import { StoreType } from "./store-types";
import useStoreTableColumns from "./useStoreTableColumns";

const StoresList = () => {
  useSetActiveTab(TAB_NAMES.STORES);
  const navigate = useNavigate();
  const [length, setLength] = useState(10);
  const [page, setPage] = useState(1);
  const [searchText, setSearchText] = useState("");
  const debouncedSearch = useDebounce(searchText);
  const { successNotification, errorNotification } = useNotification();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [idToOperate, setIdToOperate] = useState<number | undefined>(undefined);
  const { execute: handleStoreStatusChange, isSuccess: isStatusUpdateSuccess } =
    useUpdateStoreStatus();
  const {
    isLoading,
    error,
    data,
    mutate: updateStoresData,
    isValidating,
  } = useGetStores({ length, page, search: debouncedSearch });

  const {
    isLoading: isDeleteLoading,
    isSuccess: isDeleteSucceed,
    error: deleteErr,
    execute: deleteStore,
  } = useDeleteStore();

  const storesData = useMemo(() => data?.data?.rows ?? [], [data?.data]);

  const mappedStoresData = useMemo(() => {
    return storesData.map((store: StoreType) => {
      return {
        ...store,
        country: getCountryLabel(store.country),
        state: getStateLabel(store.state),
        city: getCityLabel(store.city),
        createdAt: getFormattedDate(store.createdAt),
        updatedAt: getFormattedDate(store.updatedAt),
      };
    });
  }, [storesData]);

  const totalRecords = useMemo(() => data?.data?.count, [data?.data?.count]);

  const columns = useStoreTableColumns({
    onDelete: (id: number) => {
      setIdToOperate(id);
      setIsDeleteModalOpen(true);
    },
    handleStoreStatusChange,
  });

  useEffect(() => {
    if (isDeleteSucceed) {
      updateStoresData();
      setIsDeleteModalOpen(false);
      successNotification("Store Deleted.");
    }
  }, [isDeleteSucceed, successNotification, updateStoresData]);

  useEffect(() => {
    if (deleteErr) {
      errorNotification();
    }
  }, [deleteErr, errorNotification]);

  useEffect(() => {
    if (isStatusUpdateSuccess) {
      updateStoresData();
    }
  }, [isStatusUpdateSuccess, updateStoresData]);

  return (
    <>
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        isLoading={isDeleteLoading}
        onSubmit={() => deleteStore(idToOperate)}
      />
      <div className="flex flex-col gap-[20px]">
        <TabHeader
          label="Stores Management"
          onRefresh={() => updateStoresData()}
          exportButton={{
            label: "Export",
            onClick: () => handleCsvExport(mappedStoresData),
          }}
          printButton={{
            label: "Print",
            onClick: () => window.print(),
          }}
          createButton={{
            label: "Create Store",
            onClick: () => navigate("create"),
          }}
          search={{
            onChange: (val) => setSearchText(val),
            value: searchText,
          }}
        />
        <ReactTable
          columns={columns}
          data={mappedStoresData}
          isLoading={isLoading}
          pagination={{
            setLength,
            length,
            total: totalRecords,
            page,
            setPage,
          }}
        />
      </div>
    </>
  );
};

export default StoresList;
